import { Text } from "@mantine/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  repositoryDelivery,
  repositoryPos,
} from "../../../_base/_const/_constVar";
import ImportExcelShell from "../../../common/ImportExcel/ImportExcelShell";
import {
  ImportExcelColumn,
  ImportExcelRowErrors,
  ImportExcelSelectOption,
} from "../../../common/ImportExcel/ImportExcelTypes";
import {
  parseExcelBoolean,
  parseExcelBooleanOrNull,
  toImportSubmitResult,
} from "../../../common/ImportExcel/importExcelHelper";
import { MessageResponse } from "../../../model/MessageResponse";

type CodeOption = ImportExcelSelectOption & {
  code: string;
  /** Mã trên ERP Asia (chi nhánh: CHANNELCODE, kho: ATTRIBUTE7) */
  asiaCode: string;
  /** CompanyCode của chi nhánh (attr6 của get-select-branch) */
  companyCode?: string;
};

const normalizeCode = (value: any) => String(value ?? "").trim().toUpperCase();

const sampleRows = [
  {
    invId: "01",
    subInvId: "K01_BH",
    positionCode: "BH01_MAU",
    positionName: "Vị trí bảo hành mẫu",
    active: "Đang sử dụng",
    attribute8: "BH01",
    attribute4: "Không",
  },
];

const ImportExcelView = ({ onSuccess }: { onSuccess: () => void }) => {
  const [loadingReferenceData, setLoadingReferenceData] = useState(true);
  const branchesRef = useRef<CodeOption[]>([]);
  /** Cache kho theo id chi nhánh để không phải gọi api nhiều lần */
  const warehouseCacheRef = useRef<Map<string, CodeOption[]>>(new Map());
  const warehouseLoadingRef = useRef<Map<string, Promise<CodeOption[]>>>(
    new Map()
  );

  /**
   * Lấy toàn bộ danh mục chi nhánh (không truyền auth để không bị giới hạn
   * theo phân quyền chi nhánh của người đang đăng nhập).
   */
  const fetchBranches = async () => {
    try {
      const response = await repositoryPos.get<MessageResponse<any[]>>(
        `api/v1/TblDmInventory/get-select-branch`
      );
      if (response && response.success) {
        branchesRef.current = (response.data ?? [])
          .filter((item: any) => item.value && item.att2 && item.value !== "-1")
          .map((item: any) => ({
            value: String(item.value),
            code: String(item.att2),
            asiaCode: String(item.attr9 ?? ""),
            companyCode: String(item.attr6 ?? ""),
            label: `[${item.att2}] ${item.text ?? ""}`.trim(),
          }));
      }
    } finally {
      setLoadingReferenceData(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const loadWarehouses = (branchId: string): Promise<CodeOption[]> => {
    const cached = warehouseCacheRef.current.get(branchId);
    if (cached) return Promise.resolve(cached);

    const loading = warehouseLoadingRef.current.get(branchId);
    if (loading) return loading;

    const promise = repositoryPos
      .get<MessageResponse<any[]>>(
        `/api/v1/TblDmInventory/get-select-by-branch?branchId=${branchId}`
      )
      .then((response) => {
        const options: CodeOption[] = (response?.data ?? [])
          .filter((item: any) => item.inventoryId && item.inventoryCode)
          .map((item: any) => ({
            value: String(item.inventoryId),
            code: String(item.inventoryCode),
            asiaCode: String(item.attribute7 ?? ""),
            label: `[${item.inventoryCode}] ${item.inventoryName ?? ""}`.trim(),
          }));
        // Chỉ cache khi api trả về dữ liệu, để lần sửa sau còn gọi lại được
        if (options.length > 0) warehouseCacheRef.current.set(branchId, options);
        return options;
      })
      .finally(() => warehouseLoadingRef.current.delete(branchId));

    warehouseLoadingRef.current.set(branchId, promise);
    return promise;
  };

  /**
   * Cột "Mã chi nhánh" và "Mã kho" trên file excel nhập theo MÃ, nhưng trên bảng
   * xem trước là ô chọn (select) và giá trị lưu là ID - đúng thứ api cần.
   * Mã đọc từ excel chỉ dùng để dò ra id và để báo lỗi khi không khớp.
   */
  const columns = useMemo<ImportExcelColumn[]>(
    () => [
      {
        key: "invId",
        header: "Mã chi nhánh",
        required: true,
        type: "select",
        size: 250,
        placeholder: "Chọn chi nhánh",
        options: () => branchesRef.current,
        description:
          "Nhập mã chi nhánh (ví dụ 01), hệ thống dò sang danh mục và cho chọn lại nếu không khớp",
      },
      {
        key: "invAsiaCode",
        header: "Mã chi nhánh Asia",
        size: 170,
        inTemplate: false,
        editable: false,
        description: "Lấy theo chi nhánh đã chọn, chỉ để đối chiếu",
      },
      {
        key: "subInvId",
        header: "Mã kho",
        required: true,
        type: "select",
        size: 250,
        placeholder: "Chọn kho",
        // Kho phụ thuộc chi nhánh đang chọn của chính dòng đó
        options: (row) =>
          row.invId
            ? warehouseCacheRef.current.get(String(row.invId)) ?? []
            : [],
        description:
          "Nhập mã kho thuộc chi nhánh ở trên (ví dụ K01_BH), có thể chọn lại nếu không khớp",
      },
      {
        key: "subInvAsiaCode",
        header: "Mã kho Asia",
        size: 170,
        inTemplate: false,
        editable: false,
        description: "Lấy theo kho đã chọn, chỉ để đối chiếu",
      },
      // Mã Asia của chi nhánh và kho được join lên từ danh mục (chỉ để đối chiếu),
      // vị trí kho chỉ lưu mã vị trí Asia của chính nó.
      {
        key: "positionCode",
        header: "Mã vị trí kho",
        required: true,
        size: 150,
        description: "Tối đa 15 ký tự",
      },
      {
        key: "positionName",
        header: "Tên vị trí kho",
        required: true,
        size: 260,
        description: "Tối đa 250 ký tự",
      },
      {
        key: "attribute8",
        header: "Mã vị trí Asia",
        size: 170,
        description: "Mã vị trí kho bên ERP Asia, không bắt buộc",
      },
      {
        key: "attribute4",
        header: "Lên bảng kê hàng lỗi",
        type: "boolean",
        booleanLabels: { trueLabel: "Có", falseLabel: "Không" },
        size: 170,
        description:
          "Để trống là Không. Đánh dấu để hàng nhập vào vị trí này được lên bảng kê hàng lỗi",
      },
      {
        key: "active",
        header: "Sử dụng",
        type: "boolean",
        size: 150,
        description: "Đang sử dụng | Dừng sử dụng, để trống sẽ lấy Đang sử dụng",
      },
    ],
    []
  );

  const mapRow = useCallback((raw: Record<string, any>) => {
    const invRawCode = String(raw.invId ?? "").trim();
    const subInvRawCode = String(raw.subInvId ?? "").trim();
    const branch = branchesRef.current.find(
      (b) => normalizeCode(b.code) === normalizeCode(invRawCode)
    );

    return {
      invRawCode,
      subInvRawCode,
      invId: branch?.value ?? null,
      invCode: branch?.code ?? "",
      invAsiaCode: branch?.asiaCode ?? "",
      subInvId: null as string | null,
      subInvCode: "",
      subInvAsiaCode: "",
      positionCode: raw.positionCode ?? "",
      positionName: raw.positionName ?? "",
      active: parseExcelBoolean(raw.active, true),
      attribute8: raw.attribute8 ?? "",
      // Để trống = Không. Giá trị lạ trả null để validateRows báo lỗi
      // thay vì âm thầm hiểu thành Không.
      attribute4: parseExcelBooleanOrNull(raw.attribute4, false),
    };
  }, []);

  const resolveRow = useCallback(async (row: Record<string, any>) => {
    const branch = branchesRef.current.find(
      (b) => String(b.value) === String(row.invId)
    );

    let warehouses: CodeOption[] = [];
    if (branch) warehouses = await loadWarehouses(branch.value);

    let subInvId = row.subInvId ? String(row.subInvId) : null;

    // Đổi chi nhánh thì kho cũ không còn hợp lệ
    if (subInvId && !warehouses.some((w) => String(w.value) === subInvId))
      subInvId = null;

    // Chưa có kho thì dò theo mã đọc từ excel
    if (!subInvId && normalizeCode(row.subInvRawCode).length > 0) {
      const matched = warehouses.find(
        (w) => normalizeCode(w.code) === normalizeCode(row.subInvRawCode)
      );
      subInvId = matched?.value ?? null;
    }

    const warehouse = warehouses.find((w) => String(w.value) === subInvId);

    return {
      ...row,
      invId: branch?.value ?? null,
      invCode: branch?.code ?? "",
      invAsiaCode: branch?.asiaCode ?? "",
      subInvId,
      subInvCode: warehouse?.code ?? "",
      subInvAsiaCode: warehouse?.asiaCode ?? "",
    };
  }, []);

  const validateRows = useCallback(
    (rows: Record<string, any>[]): ImportExcelRowErrors => {
      const errors: ImportExcelRowErrors = {};
      const codeSeen = new Map<string, number>();

      const addError = (index: number, key: string, message: string) => {
        if (!errors[index]) errors[index] = {};
        if (!errors[index][key]) errors[index][key] = message;
      };

      rows.forEach((row, index) => {
        const positionCode = String(row.positionCode ?? "").trim();
        const positionName = String(row.positionName ?? "").trim();

        if (!row.invId)
          addError(
            index,
            "invId",
            String(row.invRawCode ?? "").length > 0
              ? `Không khớp mã chi nhánh "${row.invRawCode}", vui lòng chọn lại`
              : "Chưa chọn chi nhánh"
          );

        if (!row.subInvId)
          addError(
            index,
            "subInvId",
            String(row.subInvRawCode ?? "").length > 0 && row.invId
              ? `Không khớp mã kho "${row.subInvRawCode}" trong chi nhánh này, vui lòng chọn lại`
              : "Chưa chọn kho"
          );

        if (positionCode.length === 0)
          addError(index, "positionCode", "Chưa nhập mã vị trí kho");
        else if (positionCode.length > 15)
          addError(index, "positionCode", "Mã vị trí kho tối đa 15 ký tự");
        else {
          const first = codeSeen.get(positionCode.toLowerCase());
          if (first !== undefined)
            addError(index, "positionCode", `Trùng mã với dòng ${first + 1}`);
          else codeSeen.set(positionCode.toLowerCase(), index);
        }

        if (String(row.attribute8 ?? "").trim().length > 500)
          addError(index, "attribute8", "Mã vị trí Asia tối đa 500 ký tự");
        // vị trí thuộc chi nhánh của công ty DVT bắt buộc phải có mã bên ERP Asia
        else if (
          branchesRef.current.find(
            (b) => String(b.value) === String(row.invId)
          )?.companyCode === "DVT" &&
          String(row.attribute8 ?? "").trim().length === 0
        )
          addError(
            index,
            "attribute8",
            "Vị trí thuộc chi nhánh công ty DVT phải nhập mã vị trí Asia"
          );

        if (row.attribute4 === null || row.attribute4 === undefined)
          addError(
            index,
            "attribute4",
            "Lên bảng kê hàng lỗi chỉ nhận Có hoặc Không",
          );

        // Tên vị trí kho được phép trùng nhau, chỉ kiểm tra độ dài
        if (positionName.length === 0)
          addError(index, "positionName", "Chưa nhập tên vị trí kho");
        else if (positionName.length > 250)
          addError(index, "positionName", "Tên vị trí kho tối đa 250 ký tự");
      });

      return errors;
    },
    []
  );

  const onSubmit = useCallback(async (rows: Record<string, any>[]) => {
    const payload = rows.map((row) => ({
      id: 0,
      invId: Number(row.invId),
      invCode: String(row.invCode ?? "").trim(),
      subInvId: Number(row.subInvId),
      subInvCode: String(row.subInvCode ?? "").trim(),
      positionCode: String(row.positionCode ?? "").trim(),
      positionName: String(row.positionName ?? "").trim(),
      active: Boolean(row.active),
      // mã vị trí kho trên hệ thống ERP Asia, để trống thì gửi null
      attribute8: String(row.attribute8 ?? "").trim() || null,
      // ATTRIBUTE4 là cột số: 1 = vị trí lưu hàng bảo hành/dịch vụ, ngược lại null
      attribute4: row.attribute4 === true ? 1 : null,
    }));

    const response = await repositoryDelivery.post(
      "/api/v1/TblDmItemPosition/create-list",
      payload
    );

    return toImportSubmitResult(response);
  }, []);

  return (
    <ImportExcelShell
      columns={columns}
      templateFileName="mau-import-danh-muc-vi-tri-kho.xlsx"
      sampleRows={sampleRows}
      mapRow={mapRow}
      resolveRow={resolveRow}
      validateRows={validateRows}
      onSubmit={onSubmit}
      onSuccess={onSuccess}
      loadingReferenceData={loadingReferenceData}
      hint={
        <Text size="xs">
          Nhập <b>Mã chi nhánh</b> và <b>Mã kho</b>, hệ thống sẽ dò sang danh mục
          để lấy id. Dòng nào không khớp sẽ báo đỏ và bạn chọn lại ngay trên bảng
          xem trước. Danh sách kho chỉ gồm các kho đang sử dụng. Vị trí thuộc
          chi nhánh của công ty <b>DVT</b> bắt buộc phải có{" "}
          <b>Mã vị trí Asia</b>.
        </Text>
      }
    />
  );
};

export default ImportExcelView;
